import {
  AssessNoteCondition,
  AssessmentControlId,
  ControlOe,
  ControlOeBasisItemId,
  ControlOeConditionId,
  ControlOeId,
  ControlOeTarget,
  CreateBasisItem,
  OeDependencyEdge,
  CreateControlOe,
  EngagementId,
  ExcludeBasisItem,
  GenerateOePlan,
  SetBasisItemCitations,
  SetBasisItemTest,
  SetControlOeConclusion,
  SetPriorConclusion,
  SetReliance,
  SetWorkOfOthersSatisfactory,
  UndependableControl,
  UpdateBasisItemSpan,
  UpdateControlOePeriod,
  UpsertControlCondition,
  UpsertNoteCondition,
} from "platformed-client/types";
import {platformedClient} from "..";
import {invalidateQueries, q} from "../../state";

export async function listForControl(assessmentControlId: AssessmentControlId): Promise<ControlOe[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/assessment-controls/{assessment_control_id}/control-oe",
    {path: {assessment_control_id: assessmentControlId}},
  );
}

export async function get(controlOeId: ControlOeId): Promise<ControlOe> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/control-oe/{control_oe_id}", {
    path: {control_oe_id: controlOeId},
  });
}

export async function create(body: CreateControlOe): Promise<ControlOe> {
  const res = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/control-oe", {body});
  await refresh();
  return res;
}

/**
 * Refresh every query an OE write can move. A control's OE state is read four
 * ways: the control's own tab (`controlOeForControl`), and engagement-wide —
 * `controlOeTargets` carries each node's conclusion, reliance and basis,
 * `controlOeDependencies` the edges resolved against targets' live conclusions,
 * and `controlOeUndependable` the controls no dependency can point at yet. A
 * write to one control can shift an edge on another, so all are refreshed
 * across the engagement, not just the control that changed.
 */
export async function refresh(): Promise<void> {
  await invalidateQueries([
    q.tprm.controlOeForControl.filter(),
    q.tprm.controlOeTargets.filter(),
    q.tprm.controlOeDependencies.filter(),
    q.tprm.controlOeUndependable.filter(),
  ]);
}

export async function addBasisItem(controlOeId: ControlOeId, body: CreateBasisItem): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/control-oe/{control_oe_id}/basis-items", {
    path: {control_oe_id: controlOeId},
    body,
  });
  await refresh();
}

export async function updateBasisItemSpan(
  controlOeId: ControlOeId,
  basisItemId: ControlOeBasisItemId,
  body: UpdateBasisItemSpan,
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/basis-items/{basis_item_id}/span",
    {path: {control_oe_id: controlOeId, basis_item_id: basisItemId}, body},
  );
  await refresh();
}

export async function setBasisItemTest(
  controlOeId: ControlOeId,
  basisItemId: ControlOeBasisItemId,
  body: SetBasisItemTest,
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/basis-items/{basis_item_id}/test",
    {path: {control_oe_id: controlOeId, basis_item_id: basisItemId}, body},
  );
  await refresh();
}

export async function createStubTest(controlOeId: ControlOeId, basisItemId: ControlOeBasisItemId): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/basis-items/{basis_item_id}/create-test",
    {path: {control_oe_id: controlOeId, basis_item_id: basisItemId}},
  );
  // The new test both links to the basis item and joins the engagement's test
  // list, so the selector's options must refresh alongside the OE state.
  await refresh();
  await invalidateQueries([q.tprm.engagementTests.filter()]);
}

export async function setPriorConclusion(
  controlOeId: ControlOeId,
  basisItemId: ControlOeBasisItemId,
  body: SetPriorConclusion,
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/basis-items/{basis_item_id}/prior-conclusion",
    {path: {control_oe_id: controlOeId, basis_item_id: basisItemId}, body},
  );
  await refresh();
}

export async function setWorkOfOthersSatisfactory(
  controlOeId: ControlOeId,
  basisItemId: ControlOeBasisItemId,
  body: SetWorkOfOthersSatisfactory,
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/basis-items/{basis_item_id}/satisfactory",
    {path: {control_oe_id: controlOeId, basis_item_id: basisItemId}, body},
  );
  await refresh();
}

export async function setBasisItemCitations(
  controlOeId: ControlOeId,
  basisItemId: ControlOeBasisItemId,
  body: SetBasisItemCitations,
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/basis-items/{basis_item_id}/citations",
    {path: {control_oe_id: controlOeId, basis_item_id: basisItemId}, body},
  );
  await refresh();
}

export async function deleteBasisItem(controlOeId: ControlOeId, basisItemId: ControlOeBasisItemId): Promise<void> {
  await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/basis-items/{basis_item_id}",
    {path: {control_oe_id: controlOeId, basis_item_id: basisItemId}},
  );
  await refresh();
}

export async function addNoteCondition(
  controlOeId: ControlOeId,
  basisItemId: ControlOeBasisItemId,
  body: UpsertNoteCondition,
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/basis-items/{basis_item_id}/note-conditions",
    {path: {control_oe_id: controlOeId, basis_item_id: basisItemId}, body},
  );
  await refresh();
}

export async function updateNoteCondition(
  controlOeId: ControlOeId,
  conditionId: ControlOeConditionId,
  body: UpsertNoteCondition,
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/conditions/{condition_id}",
    {path: {control_oe_id: controlOeId, condition_id: conditionId}, body},
  );
  await refresh();
}

export async function assessNoteCondition(
  controlOeId: ControlOeId,
  conditionId: ControlOeConditionId,
  body: AssessNoteCondition,
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/conditions/{condition_id}/assessment",
    {path: {control_oe_id: controlOeId, condition_id: conditionId}, body},
  );
  await refresh();
}

export async function deleteCondition(controlOeId: ControlOeId, conditionId: ControlOeConditionId): Promise<void> {
  await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/conditions/{condition_id}",
    {path: {control_oe_id: controlOeId, condition_id: conditionId}},
  );
  await refresh();
}

export async function addControlCondition(
  controlOeId: ControlOeId,
  basisItemId: ControlOeBasisItemId,
  body: UpsertControlCondition,
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/basis-items/{basis_item_id}/control-conditions",
    {path: {control_oe_id: controlOeId, basis_item_id: basisItemId}, body},
  );
  await refresh();
}

export async function listTargets(engagementId: EngagementId): Promise<ControlOeTarget[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/control-oe",
    {path: {engagement_id: engagementId}},
  );
}

export async function listUndependable(engagementId: EngagementId): Promise<UndependableControl[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/control-oe-undependable",
    {path: {engagement_id: engagementId}},
  );
}

export async function listDependencyEdges(engagementId: EngagementId): Promise<OeDependencyEdge[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/control-oe-dependencies",
    {path: {engagement_id: engagementId}},
  );
}

export async function setConclusion(controlOeId: ControlOeId, body: SetControlOeConclusion): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/control-oe/{control_oe_id}/conclusion", {
    path: {control_oe_id: controlOeId},
    body,
  });
  await refresh();
}

export async function setReliance(controlOeId: ControlOeId, body: SetReliance): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/control-oe/{control_oe_id}/reliance", {
    path: {control_oe_id: controlOeId},
    body,
  });
  await refresh();
}

export async function excludeBasisItem(
  controlOeId: ControlOeId,
  basisItemId: ControlOeBasisItemId,
  body: ExcludeBasisItem,
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/basis-items/{basis_item_id}/exclusion",
    {path: {control_oe_id: controlOeId, basis_item_id: basisItemId}, body},
  );
  await refresh();
}

export async function reincludeBasisItem(controlOeId: ControlOeId, basisItemId: ControlOeBasisItemId): Promise<void> {
  await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/basis-items/{basis_item_id}/exclusion",
    {path: {control_oe_id: controlOeId, basis_item_id: basisItemId}},
  );
  await refresh();
}

export async function updatePeriod(controlOeId: ControlOeId, body: UpdateControlOePeriod): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/control-oe/{control_oe_id}/period", {
    path: {control_oe_id: controlOeId},
    body,
  });
  await refresh();
}

export async function generatePlan(controlOeId: ControlOeId, body: GenerateOePlan): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/control-oe/{control_oe_id}/plan", {
    path: {control_oe_id: controlOeId},
    body,
  });
  await refresh();
}

export async function acceptPlan(controlOeId: ControlOeId): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/control-oe/{control_oe_id}/plan/accept", {
    path: {control_oe_id: controlOeId},
  });
  await refresh();
}

export async function discardPlan(controlOeId: ControlOeId): Promise<void> {
  await platformedClient.internalRequest("delete", "/region/{region}/api/tprm/control-oe/{control_oe_id}/plan", {
    path: {control_oe_id: controlOeId},
  });
  await refresh();
}

export async function generateConclusion(controlOeId: ControlOeId): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/conclusion/generate",
    {path: {control_oe_id: controlOeId}},
  );
  await refresh();
}

export async function acceptConclusion(controlOeId: ControlOeId): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/conclusion/accept",
    {path: {control_oe_id: controlOeId}},
  );
  await refresh();
}

export async function discardConclusion(controlOeId: ControlOeId): Promise<void> {
  await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/control-oe/{control_oe_id}/conclusion/draft",
    {path: {control_oe_id: controlOeId}},
  );
  await refresh();
}
